import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Toolbar from "@mui/material/Toolbar";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import { ButtonUI, KMKDrawer } from "../../../../Common";
import CollapsibleVirtulizedList from "./CollapsibleVirtulizedList";

const useStyles = makeStyles()((theme) => ({
  ListItemButton: {
    wordBreak: "break-all",
    padding: "4px 8px",
  },
  ListItemText: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
  },
  IconButton: {
    borderRadius: "2px",
    background: theme.palette.primary.main,
    color: "#fff",
  },
  fieldType: {
    minWidth: "100px",
  },
  heading: {
    paddingLeft: "15px",
    color: "#DF672A",
    fontWeight: "bold",
    fontSize: "1rem",
    wordBreak: "break-all",
    "& span": {
      fontWeight: "normal",
    },
  },
  DrawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: "15px",
    paddingBottom: "0",
  },
}));

const drawerWidth = 420;

const AddMapping = ({ mappings, currentMapping, addMapping, selectedMappings, entityName, fetchDSRequiredInfo }) => {
  const { classes } = useStyles();
  const [openedList, setOpenedList] = useState(
    currentMapping.mappings ? currentMapping.mappings.map((mapping) => mapping.dataSource) : []
  );
  const [selectedMapping, setSelectedMapping] = useState([]);
  const [prevMappings, setPrevMappings] = useState(selectedMappings || []);
  const [search, setSearch] = useState("");

  const onClose = useCallback(() => {
    addMapping(currentMapping.mappings || []);
  }, [addMapping, currentMapping.mappings]);

  const onSaveClick = useCallback(() => {
    addMapping(selectedMapping);
  }, [addMapping, selectedMapping]);

  const handleClick = useCallback((dataSource) => {
    setOpenedList((prevState) => {
      if (prevState.some((mapping) => mapping === dataSource)) {
        return prevState.filter((mapping) => !(mapping === dataSource));
      } else {
        return [...prevState, dataSource];
      }
    });
  }, []);

  const handleSelect = useCallback(
    (dataSource, field, type, length) => {
      if (selectedMapping.some((mapping) => mapping.dataSource === dataSource && mapping.field === field)) {
        setSelectedMapping(
          selectedMapping.filter((mapping) => !(mapping.dataSource === dataSource && mapping.field === field))
        );
        setPrevMappings(
          prevMappings.filter((mapping) => !(mapping.dataSource === dataSource && mapping.field === field))
        );
      } else {
        setSelectedMapping([
          ...selectedMapping,
          {
            dataSource,
            field,
            type,
            length,
          },
        ]);
      }
    },
    [prevMappings, selectedMapping]
  );

  const checkDisabled = useCallback(
    (dataSource, field, dSFieldMapping) => {
      return (
        (Object.keys(dSFieldMapping).length > 0 &&
          dSFieldMapping?.entity.toLowerCase() !== entityName.toLowerCase() &&
          currentMapping.fieldName.toLowerCase() !== dSFieldMapping?.entity.toLowerCase()) ||
        prevMappings.some((mapping) => mapping.dataSource === dataSource && mapping.field === field) ||
        selectedMapping.some((mapping) => mapping.dataSource === dataSource && mapping.field !== field)
      );
    },
    [currentMapping.fieldName, entityName, prevMappings, selectedMapping]
  );

  const checkSelected = useCallback(
    (dataSource, field) => {
      return (
        (field.hasOwnProperty("isDeleted") && field.isDeleted.length > 0) ||
        selectedMapping.some((mapping) => mapping.dataSource === dataSource && mapping.field === field)
      );
    },
    [selectedMapping]
  );

  const isDefaultOpen = useCallback((dataSource) => openedList.some((mapping) => mapping === dataSource), [openedList]);

  const failedFields = useMemo(() => {
    if (!selectedMapping.length) return [];

    return selectedMapping.filter((mapping) => {
      return !(
        mapping.type === currentMapping.fieldType &&
        (!(mapping.length && currentMapping.fieldLength) ||
          (mapping.length && currentMapping.fieldLength && mapping.length <= currentMapping.fieldLength))
      );
    });
  }, [selectedMapping, currentMapping.fieldType, currentMapping.fieldLength]);

  useEffect(() => {
    const getData = async () => await fetchDSRequiredInfo();
    if (!mappings) getData();
  }, [fetchDSRequiredInfo, mappings]);

  useEffect(() => {
    if (mappings) {
      //Adding length and type to current mappings
      const _currentMapping = [...currentMapping.mappings];
      mappings.forEach(({ name, fields }) => {
        const _mapping = _currentMapping.find(({ dataSource }) => dataSource === name);
        if (_mapping) {
          const _field = fields.find((field) => field.name === _mapping.field);
          if (_field) {
            _mapping.length = _field.type === "Reference" ? _field.referenceFrom.field.length : _field.length;
            _mapping.type = _field.type === "Reference" ? _field.referenceFrom.field.type : _field.type;
          }
        }
      });

      setSelectedMapping(_currentMapping);
    }
  }, [mappings]);

  return (
    <KMKDrawer
      anchor="right"
      open={true}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "450px" },
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        sx={{ position: "sticky", right: 12, top: 44, margin: 0, padding: 0, zIndex: 10000, backgroundColor: "white" }}
      >
        <Box className={classes.DrawerHeader}>
          <Typography color="primary" sx={{ flexGrow: "1" }}>
            Entity Mapping
          </Typography>
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography className={classes.heading} variant="h6" gutterBottom component="div">
          Attribute :
          <span>
            {currentMapping.fieldName}{" "}
            {currentMapping.fieldType &&
              `(${currentMapping.fieldType}${currentMapping.fieldLength ? ` - ${currentMapping.fieldLength}` : ""})`}
          </span>
        </Typography>
        <Divider />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <TextField
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search attribute"
            variant="outlined"
            InputProps={{
              startAdornment: <SearchIcon color="primary" />,
              ...(search && {
                endAdornment: (
                  <IconButton onClick={() => setSearch("")} size="small" sx={{ p: 0 }}>
                    <ClearIcon color="primary" />
                  </IconButton>
                ),
              }),
            }}
            sx={{ marginX: 2, marginTop: 1, width: "90%" }}
          />
        </Box>
      </Box>
      <Toolbar />
      <Box sx={{ width: 440, padding: "0px 10px 20px" }} role="presentation">
        {mappings ? (
          mappings.map(({ name, fields }) => {
            return (
              <div key={name}>
                <ListItemButton onClick={() => handleClick(name)} className={classes.ListItemButton}>
                  {isDefaultOpen(name) ? (
                    <RemoveIcon className={classes.IconButton} />
                  ) : (
                    <AddIcon className={classes.IconButton} />
                  )}
                  <ListItemText sx={{ ml: 2 }} primary={name} />
                </ListItemButton>
                <CollapsibleVirtulizedList
                  currentMapping={currentMapping}
                  name={name}
                  fields={fields.filter((field) => field.name.toLowerCase().includes(search.toLowerCase()))}
                  checkDisabled={checkDisabled}
                  handleSelect={handleSelect}
                  isDefaultOpen={isDefaultOpen}
                  checkSelected={checkSelected}
                  failedFields={failedFields}
                />
              </div>
            );
          })
        ) : (
          <></>
        )}
      </Box>
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          position: "sticky",
          right: 12,
          bottom: 0,
          zIndex: 10000,
          backgroundColor: "white",
          padding: "15px",
          flexGrow: "1",
        }}
      >
        <Box>
          <ButtonUI
            disabled={!!failedFields.length}
            variant="contained"
            color="success"
            sx={{ mr: "10px" }}
            onClick={onSaveClick}
          >
            Save
          </ButtonUI>
          <ButtonUI variant="contained" color="secondary" onClick={onClose}>
            Cancel
          </ButtonUI>
        </Box>
      </Paper>
    </KMKDrawer>
  );
};

export default AddMapping;
